import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import PublicLayout from "@/components/public/PublicLayout";
import InquiryDialog from "@/components/public/InquiryDialog";
import { publicApi, fileUrl } from "@/lib/api";
import { money, partCategoryLabel, partOriginLabel } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, ShieldCheck, Wrench, PackageCheck, PackageX } from "lucide-react";

function Spec({ label, value }) {
  if (value === undefined || value === null || value === "" || (Array.isArray(value) && value.length === 0)) return null;
  return (
    <div className="py-2 border-b border-[var(--border-2)] flex justify-between gap-6">
      <dt className="text-[var(--text-2)] text-sm">{label}</dt>
      <dd className="text-[var(--text)] text-sm text-right">{Array.isArray(value) ? value.join(", ") : value}</dd>
    </div>
  );
}

export default function PartDetail() {
  const { id } = useParams();
  const [part, setPart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [active, setActive] = useState(0);

  useEffect(() => {
    setLoading(true);
    publicApi.get(`/public/parts/${id}`)
      .then((r) => setPart(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <PublicLayout><div className="max-w-6xl mx-auto px-4 py-16 text-[var(--muted)]" data-testid="part-detail-loading">Loading…</div></PublicLayout>;
  if (notFound || !part) return (
    <PublicLayout>
      <div className="max-w-6xl mx-auto px-4 py-20 text-center" data-testid="part-detail-notfound">
        <Wrench size={40} className="mx-auto text-[var(--border-2)] mb-3" />
        <h1 className="text-2xl font-semibold text-[var(--text)]">Part not found</h1>
        <Link to="/parts" className="text-[#c9a86a] mt-3 inline-block">Back to Parts</Link>
      </div>
    </PublicLayout>
  );

  const photos = part.public_photos || [];
  const mainPhoto = photos[active] || photos[0];
  const showPrice = !part.price_on_request && part.price != null;
  const inStock = part.status !== "sold_out" && (part.quantity || 0) > 0;

  return (
    <PublicLayout>
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="part-detail">
        <Link to="/parts" className="inline-flex items-center gap-2 text-sm text-[var(--text-2)] hover:text-[var(--text)] mb-6"><ArrowLeft size={16} /> Back to Parts</Link>
        <div className="grid lg:grid-cols-2 gap-10">
          {/* Gallery */}
          <div>
            <div className="aspect-square bg-[var(--panel-2)] border border-[var(--border-2)] overflow-hidden flex items-center justify-center">
              {mainPhoto ? (
                <img src={fileUrl(mainPhoto.url || mainPhoto)} alt={part.title} className="w-full h-full object-cover" data-testid="part-main-photo" />
              ) : (
                <Wrench size={64} className="text-[var(--border-2)]" />
              )}
            </div>
            {photos.length > 1 && (
              <div className="grid grid-cols-5 gap-2 mt-3">
                {photos.map((p, i) => (
                  <button key={p.id || i} onClick={() => setActive(i)} data-testid={`part-thumb-${i}`}
                    className={`aspect-square overflow-hidden border ${i === active ? "border-[#c9a86a]" : "border-[var(--border-2)]"}`}>
                    <img src={fileUrl(p.url || p)} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Badge variant="outline" className="text-[#c9a86a] border-[#c9a86a]/40 text-[11px] uppercase tracking-wide">{partCategoryLabel(part.part_category)}</Badge>
              {part.origin === "oem" && <Badge variant="outline" className="text-[#c9a86a] border-[#c9a86a]/40 text-[11px]"><ShieldCheck size={11} className="mr-1" /> OEM</Badge>}
              {part.origin === "aftermarket" && <Badge variant="outline" className="text-[var(--text-2)] border-[var(--border-2)] text-[11px]">Aftermarket</Badge>}
            </div>
            <h1 className="text-2xl sm:text-3xl font-semibold text-[var(--text)] leading-tight" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="part-title">{part.title}</h1>

            <div className="mt-4 flex items-center gap-3">
              <span className="text-xl text-[#c9a86a] font-medium" data-testid="part-price">{showPrice ? money(part.price) : "Price on Request"}</span>
              {inStock ? (
                <span className="inline-flex items-center gap-1 text-emerald-400 text-sm" data-testid="part-availability"><PackageCheck size={15} /> In stock{part.quantity ? ` (${part.quantity} available)` : ""}</span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[var(--text-2)] text-sm" data-testid="part-availability"><PackageX size={15} /> {part.status === "reserved" ? "Reserved" : "Currently unavailable"}</span>
              )}
            </div>

            {part.description && <p className="mt-5 text-[var(--text-2)] leading-relaxed" data-testid="part-description">{part.description}</p>}

            <dl className="mt-6">
              <Spec label="Brand" value={part.brand} />
              <Spec label="Model" value={part.model} />
              <Spec label="Reference" value={part.reference_number} />
              <Spec label="Part Number" value={part.part_number} />
              <Spec label="Compatible References" value={part.compatible_references} />
              <Spec label="Compatible Models" value={part.compatible_models} />
              <Spec label="Condition" value={part.condition} />
              <Spec label="Origin" value={partOriginLabel(part.origin)} />
            </dl>

            <div className="mt-8">
              <InquiryDialog part={part} label="Inquire / Request" />
              <p className="text-xs text-[var(--text-2)] mt-2">Questions about fitment or want to reserve this part? Send a request and the house will reply.</p>
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
